import React, { FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Button, Message } from '@afterpaytouch/core'
import { useConsumerSelector, useCountry, useIdentityLicenseMutation, useIsPartialVerified } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { getInitialFormData, getLicenceCardNumberValidation, getLicenceValidations, getSchema, isLicenceNumValid, setHiddenFieldsByCountry } from './utils'
import { Terms } from './Terms'
import { DisabledModal, FormWrapper } from '..'
import FormBuilder from '@afterpaytouch/form'
import type { FieldErrors } from '@afterpaytouch/form'
import { DriversLicenceForm, LicenceNumOpts, SchemaUpdates } from './types'
import { isDifferentWithProfileDOB, isDifferentWithProfileNames, IsRetryPageContext, useSetErrorsAndLogEvent } from '../VerifyIdentity/helper'
import { DocumentProps } from '../../model'
import { Channel, IdentityDocumentVerificationStatus, IdentityLicenseRequest } from '@afterpaytouch/portal-api/identity/types'
import { joinStrings } from '@afterpay/utils/string'
import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { getIdentityVerifyErrorCode, IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import { compareObjects } from '../../utils/comparison'
import { USER_VERIFICATION_ERRORS } from '../VerifyIdentity/constants'
import { useCountryStateIsShown } from '../../state/identity/hooks'
import { HardIdProfileCard, ErrorMessage, HardIdHelpLink, HardIdHeader } from '../VerifyIdentity/components'

export const DriversLicence: FunctionComponent<DocumentProps> = ({ onSuccess = () => {}, onFailure = () => {} }) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [lock, setLock] = useState(false)
  const [error, setError] = useState<IdentityErrorCode | null>(null)
  const { buttonSize } = useCheckoutSizes()
  const { t } = useTranslation()
  const consumer = useConsumerSelector()
  const country = useCountry()
  const { accountCountry = country, profile } = consumer
  const { state: initialState } = getInitialFormData(consumer)
  const lastSelectedState = useRef(initialState)
  const showCardNumber = accountCountry === Country.AU
  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    licenceNumOpts: {} as LicenceNumOpts,
    cardNumOpts: {} as LicenceNumOpts,
    showProfileDobAlert: false,
    showProfileNameAlert: false,
    licenceMaxLength: 0,
  })
  const [submittedRequest, setSubmittedRequest] = useState<IdentityLicenseRequest | undefined>(undefined)
  const [identifyVerifyLicence] = useIdentityLicenseMutation()
  const profileDOB = profile?.dateOfBirth ?? ''
  const countryStateIsShowing = useCountryStateIsShown()
  const isPartialVerified = useIsPartialVerified()
  const isRetryPage = useContext(IsRetryPageContext)
  const isProfileEditable = !(isPartialVerified && isRetryPage)
  const verifyButtonAnalytics =
    isPartialVerified && isRetryPage ? TrackingEvent.PRESSED_HARDID_RETRY_PAGE_VERIFY_BUTTON : TrackingEvent.PRESSED_HARDID_DRIVERS_LICENCE_PAGE_VERIFY_BUTTON
  const setErrorsAndLogEvent = useSetErrorsAndLogEvent(setError, setLock, TrackingEvent.DRIVER_LICENCE_SUBMIT_FAILED)

  const { schema, initialFormData } = useMemo(() => {
    const onNameBlur = (): void => {
      const { givenNames = '', otherGivenNames = '', surname = '' } = formData
      const fullName = joinStrings([givenNames, otherGivenNames, surname])

      setSchemaUpdates((currSchema) => ({
        ...currSchema,
        showProfileNameAlert: isDifferentWithProfileNames(fullName, profile),
      }))
    }
    const components = {
      profileCard: () => <HardIdProfileCard />,
      terms: () => <Terms />,
      footer: () => {
        return (
          <Button.Primary loading={lock} size={buttonSize} type='submit' disabled={lock} padding='Fluid'>
            {t('verifyIdentity:licence:cta')}
          </Button.Primary>
        )
      },
      dobAlert: () => (
        <Message
          kind='Success'
          iconName='Profile'
          heading={t('verifyIdentity:alert:profileUpdateHeading')}
          description={t('verifyIdentity:alert:profileUpdateDOB')}
        />
      ),
      nameAlert: () => (
        <Message
          kind='Success'
          iconName='Profile'
          heading={t('verifyIdentity:alert:profileUpdateHeading')}
          description={t('verifyIdentity:alert:profileUpdateName')}
        />
      ),
    }
    const isUserFormError = USER_VERIFICATION_ERRORS.includes(error as IdentityErrorCode)
    return getSchema(consumer, components, t, schemaUpdates, onNameBlur, isUserFormError, isProfileEditable)
  }, [consumer, t, schemaUpdates, buttonSize, lock, profile, error, isProfileEditable])
  const [formData, setFormData] = useState(initialFormData)

  setHiddenFieldsByCountry(schema, accountCountry)

  const onSubmit = async (driversLicenceData: DriversLicenceForm): Promise<void> => {
    setErrorsAndLogEvent({ lock: true, event: verifyButtonAnalytics })
    const { dlCardNumber, ...driverLicence } = driversLicenceData

    // NZ does not have a state of issuer however, IdentityLicenseRequest needs one
    // so default to consumer state
    const request: IdentityLicenseRequest = {
      channel: Channel.CHECKOUT,
      countryCode: accountCountry,
      state: initialState ?? '',
      ...driverLicence,
    }

    if (showCardNumber) {
      request.dlCardNumber = dlCardNumber
    }

    if (compareObjects(request, submittedRequest)) {
      setErrorsAndLogEvent({ error: IdentityErrorCode.IdentityAlreadyRejected, lock: false })
      return
    }

    try {
      const response = await identifyVerifyLicence(request).unwrap()
      if (response.status === IdentityDocumentVerificationStatus.VERIFIED) {
        onSuccess()
      } else if (response.status === IdentityDocumentVerificationStatus.REJECTED) {
        setSubmittedRequest(request)
        setErrorsAndLogEvent({ error: IdentityErrorCode.IdentityCheckRejected, lock: false })
      }
    } catch (e) {
      const error = getIdentityVerifyErrorCode(e)
      if ([ErrorCode.MaxAttemptsReached, ErrorCode.MaxAttemptsReachedForType].includes(e?.data?.errorCode)) {
        logEvent(TrackingEvent.DRIVER_LICENCE_SUBMIT_FAILED, { idvError: e?.data?.errorCode })
        return onFailure()
      }
      setErrorsAndLogEvent({ error: error ?? IdentityErrorCode.Default, lock: false })
    }
  }

  const onError = (errors: FieldErrors<DriversLicenceForm>): void => {
    console.log('error', errors)
  }

  useEffect(() => {
    if (typeof error !== 'undefined') {
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [error])

  const onChange = (data: DriversLicenceForm): void => {
    setError(null)
    setFormData(data)
    // state might be undefined for places like NZ (formbuilder doesnt register hidden fields)
    // so default to consumer state
    const { state = initialState, dateOfBirth, licenceNumber, dlCardNumber } = data
    const { licenceNumOpts, licenceMaxLength } = getLicenceValidations(accountCountry, state ?? '', t, countryStateIsShowing)
    const cardNumOpts = getLicenceCardNumberValidation(accountCountry, state ?? '', t, countryStateIsShowing)
    const didStateUpdate = lastSelectedState.current !== state
    // licence num should not clear for countries with no state selection
    const shouldLicenceNumClear = didStateUpdate && countryStateIsShowing && !isLicenceNumValid(licenceNumber, licenceNumOpts) && licenceNumber.length > 0

    const shouldLicenceCardNumClear =
      didStateUpdate && countryStateIsShowing && showCardNumber && dlCardNumber?.length > 0 && !isLicenceNumValid(dlCardNumber, cardNumOpts)

    setSchemaUpdates((currSchema) => ({
      ...currSchema,
      showProfileDobAlert: isDifferentWithProfileDOB(dateOfBirth, profileDOB),
      licenceNumOpts,
      licenceMaxLength,
      cardNumOpts,
      // using a random key to clear the licence num field
      ...(shouldLicenceNumClear && { licenceNumberKey: Math.random().toString(36) }),
      ...(shouldLicenceCardNumClear && { licenceCardNumberKey: Math.random().toString(36) }),
    }))

    lastSelectedState.current = state
  }

  return (
    <>
      <HardIdHeader documentType='licence' />
      <ErrorMessage identityType='licence' error={error} />
      <FormWrapper>
        <FormBuilder schema={schema} testNameSpace={'verify-licence'} onChange={onChange} onSubmit={onSubmit} onError={onError} disabled={lock} />
      </FormWrapper>
      <HardIdHelpLink />
      {lock && <DisabledModal />}
    </>
  )
}
